<template>
    <v-container fluid>
        <!-- Loading state -->
        <v-row v-if="loading" justify="center" class="pa-12">
            <v-progress-circular
                indeterminate
                color="primary"
                size="64"
            ></v-progress-circular>
        </v-row>

        <!-- Content -->
        <template v-else-if="report">
            <!-- Header Card -->
            <v-card class="mb-4">
                <v-card-title>
                    <v-btn icon class="mr-2" @click="goBack">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-icon left>mdi-file-document</v-icon>
                    {{ report.report_code }}
                    <v-spacer></v-spacer>
                    <v-chip :color="getStatusColor(report.status)" class="mr-2">
                        {{ getStatusLabel(report.status) }}
                    </v-chip>
                </v-card-title>

                <v-card-subtitle>
                    <v-row class="mt-2">
                        <v-col cols="12" md="3">
                            <strong>Data Generazione:</strong><br>
                            {{ formatDate(report.generated_date) }}
                        </v-col>
                        <v-col cols="12" md="3">
                            <strong>Periodo Analisi:</strong><br>
                            {{ formatDate(report.analysis_start_date) }} - {{ formatDate(report.analysis_end_date) }}
                        </v-col>
                        <v-col cols="12" md="3">
                            <strong>Prodotti da Riordinare:</strong><br>
                            <span v-if="ratingFilter.length > 0 || showOnlyCritical">
                                <v-chip x-small color="primary" class="mr-1">Filtrato</v-chip>
                                {{ filteredTotals.products }} / {{ report.products_to_reorder }}
                            </span>
                            <span v-else>
                                {{ report.products_to_reorder }} / {{ report.total_products }}
                            </span>
                        </v-col>
                        <v-col cols="12" md="3">
                            <strong>Costo Stimato:</strong><br>
                            <span v-if="ratingFilter.length > 0 || showOnlyCritical">
                                <v-chip x-small color="primary" class="mr-1">Filtrato</v-chip>
                                {{ formatEuro(filteredTotals.cost) }}
                            </span>
                            <span v-else>
                                {{ formatEuro(report.total_estimated_cost) }}
                            </span>
                        </v-col>
                    </v-row>
                </v-card-subtitle>
            </v-card>

            <!-- Toolbar -->
            <v-card class="mb-4">
                <v-card-text>
                    <v-row align="center">
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="ratingFilter"
                                :items="ratingOptions"
                                item-text="text"
                                item-value="value"
                                label="Filtra per Rating ABC/FSN"
                                prepend-icon="mdi-star"
                                multiple
                                chips
                                deletable-chips
                                clearable
                                dense
                                outlined
                                hide-details
                            >
                                <template v-slot:selection="{ item, index }">
                                    <v-chip
                                        v-if="index < 2"
                                        small
                                        :color="getRatingColor(item.value)"
                                        dark
                                        close
                                        @click:close="ratingFilter.splice(ratingFilter.indexOf(item.value), 1)"
                                    >
                                        {{ item.value }}
                                    </v-chip>
                                    <span v-if="index === 2" class="grey--text text-caption">
                                        (+{{ ratingFilter.length - 2 }} altri)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="2">
                            <v-switch
                                v-model="showOnlyCritical"
                                label="Solo critici"
                                dense
                                hide-details
                            ></v-switch>
                        </v-col>
                        <v-col cols="12" md="2">
                            <v-chip v-if="selectedItems.length > 0" color="info" outlined>
                                <v-icon left small>mdi-checkbox-marked</v-icon>
                                {{ selectedItems.length }} selezionati
                            </v-chip>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                            <v-btn
                                text
                                small
                                @click="resetFilters"
                                v-if="ratingFilter.length > 0 || showOnlyCritical"
                                class="mr-2"
                            >
                                <v-icon left small>mdi-filter-off</v-icon>
                                Reset Filtri
                            </v-btn>
                            <v-btn
                                color="success"
                                @click="exportExcel"
                                :loading="exporting"
                            >
                                <v-icon left>mdi-microsoft-excel</v-icon>
                                Esporta Excel
                                <span v-if="selectedItems.length > 0" class="ml-1">({{ selectedItems.length }})</span>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Main Content with Tabs -->
            <v-card>
                <v-tabs v-model="tab">
                    <v-tab>Per Fornitore</v-tab>
                    <v-tab>Tutti i Prodotti</v-tab>
                    <v-tab>Per Rating</v-tab>
                    <v-tab>Solo Critici</v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab">
                    <!-- Tab: By Supplier -->
                    <v-tab-item>
                        <v-expansion-panels v-if="reportDetails" class="ma-4">
                            <v-expansion-panel v-for="supplier in filteredSuppliers" :key="supplier.supplier_id">
                                <v-expansion-panel-header>
                                    <v-row align="center" no-gutters>
                                        <v-col cols="4">
                                            <strong>{{ supplier.supplier_name }}</strong><br>
                                            <small>Codice: {{ supplier.supplier_code }}</small>
                                        </v-col>
                                        <v-col cols="2">
                                            <v-chip small>{{ supplier.products_count }} prodotti</v-chip>
                                        </v-col>
                                        <v-col cols="2">
                                            <v-chip small color="warning" v-if="supplier.critical_count > 0">
                                                {{ supplier.critical_count }} critici
                                            </v-chip>
                                        </v-col>
                                        <v-col cols="2">
                                            Qtà: {{ supplier.total_qty }}
                                        </v-col>
                                        <v-col cols="2">
                                            <strong>{{ formatEuro(supplier.total_cost) }}</strong>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-data-table
                                        v-model="selectedItems"
                                        :headers="itemHeadersSupplier"
                                        :items="supplier.items"
                                        dense
                                        hide-default-footer
                                        :items-per-page="-1"
                                        show-select
                                        item-key="id"
                                    >
                                        <template v-slot:item.ar_codart="{ item }">
                                            <router-link
                                                :to="{ name: 'product', query: { id: item.ar_codart } }"
                                                class="text-decoration-none"
                                            >
                                                <strong class="primary--text">{{ item.ar_codart }}</strong>
                                            </router-link>
                                        </template>

                                        <template v-slot:item.is_critical="{ item }">
                                            <v-icon small color="error" v-if="item.is_critical">mdi-alert</v-icon>
                                        </template>

                                        <template v-slot:item.rating="{ item }">
                                            <v-tooltip bottom v-if="item.rating">
                                                <template v-slot:activator="{ on }">
                                                    <v-chip
                                                        small
                                                        :color="getRatingColor(item.rating)"
                                                        dark
                                                        v-on="on"
                                                    >
                                                        {{ item.rating }}
                                                    </v-chip>
                                                </template>
                                                <div>
                                                    <strong>{{ getRatingDescription(item.rating) }}</strong><br>
                                                    <span v-if="item.abc_class">ABC: {{ item.abc_class }} | FSN: {{ item.fsn_class }}</span><br>
                                                    <span>Moltiplicatore: {{ item.rating_multiplier }}x</span>
                                                </div>
                                            </v-tooltip>
                                        </template>

                                        <template v-slot:item.days_of_stock="{ item }">
                                            <v-chip small :color="getDaysStockColor(item.days_of_stock)">
                                                {{ Math.round(item.days_of_stock) }} gg
                                            </v-chip>
                                        </template>

                                        <template v-slot:item.final_order_qty="{ item }">
                                            <strong class="primary--text">{{ item.final_order_qty || item.adjusted_order_qty }}</strong>
                                        </template>

                                        <template v-slot:item.carton_order_qty="{ item }">
                                            <v-tooltip bottom v-if="item.carton_qty > 1">
                                                <template v-slot:activator="{ on }">
                                                    <strong class="success--text" v-on="on">
                                                        {{ getCartonQuantity(item) }}
                                                    </strong>
                                                </template>
                                                <span>Cartone da {{ item.carton_qty }} pz</span>
                                            </v-tooltip>
                                            <span v-else>{{ getCartonQuantity(item) }}</span>
                                        </template>

                                        <template v-slot:item.estimated_cost="{ item }">
                                            {{ formatEuro(item.estimated_cost) }}
                                        </template>
                                    </v-data-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-tab-item>

                    <!-- Tab: All Products (Global View) -->
                    <v-tab-item>
                        <v-data-table
                            v-if="report.items"
                            v-model="selectedItems"
                            :headers="itemHeadersGlobal"
                            :items="filteredProducts"
                            :search="search"
                            class="ma-4"
                            show-select
                            item-key="id"
                        >
                            <template v-slot:top>
                                <v-row class="ma-2">
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="search"
                                            label="Cerca prodotto..."
                                            prepend-inner-icon="mdi-magnify"
                                            clearable
                                            outlined
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </template>

                            <template v-slot:item.ar_codart="{ item }">
                                <router-link
                                    :to="{ name: 'product', query: { id: item.ar_codart } }"
                                    class="text-decoration-none"
                                >
                                    <strong class="primary--text">{{ item.ar_codart }}</strong>
                                </router-link>
                            </template>

                            <template v-slot:item.is_critical="{ item }">
                                <v-icon small color="error" v-if="item.is_critical">mdi-alert</v-icon>
                            </template>

                            <template v-slot:item.rating="{ item }">
                                <v-tooltip bottom v-if="item.rating">
                                    <template v-slot:activator="{ on }">
                                        <v-chip
                                            small
                                            :color="getRatingColor(item.rating)"
                                            dark
                                            v-on="on"
                                        >
                                            {{ item.rating }}
                                        </v-chip>
                                    </template>
                                    <div>
                                        <strong>{{ getRatingDescription(item.rating) }}</strong><br>
                                        <span v-if="item.abc_class">ABC: {{ item.abc_class }} | FSN: {{ item.fsn_class }}</span><br>
                                        <span>Moltiplicatore: {{ item.rating_multiplier }}x</span>
                                    </div>
                                </v-tooltip>
                            </template>

                            <template v-slot:item.days_of_stock="{ item }">
                                <v-chip small :color="getDaysStockColor(item.days_of_stock)">
                                    {{ Math.round(item.days_of_stock) }} gg
                                </v-chip>
                            </template>

                            <template v-slot:item.ml_suggested_order_qty="{ item }">
                                <span v-if="item.ml_suggested_order_qty !== null">
                                    {{ item.ml_suggested_order_qty }}
                                    <v-icon
                                        x-small
                                        :color="item.ml_vs_traditional_diff > 0 ? 'success' : (item.ml_vs_traditional_diff < 0 ? 'error' : 'grey')"
                                    >
                                        {{ item.ml_vs_traditional_diff > 0 ? 'mdi-arrow-up' : (item.ml_vs_traditional_diff < 0 ? 'mdi-arrow-down' : 'mdi-minus') }}
                                    </v-icon>
                                </span>
                                <span v-else class="grey--text">-</span>
                            </template>

                            <template v-slot:item.final_order_qty="{ item }">
                                <strong class="primary--text">{{ item.final_order_qty || item.adjusted_order_qty }}</strong>
                            </template>

                            <template v-slot:item.carton_order_qty="{ item }">
                                <v-tooltip bottom v-if="item.carton_qty > 1">
                                    <template v-slot:activator="{ on }">
                                        <strong class="success--text" v-on="on">
                                            {{ getCartonQuantity(item) }}
                                        </strong>
                                    </template>
                                    <span>Cartone da {{ item.carton_qty }} pz</span>
                                </v-tooltip>
                                <span v-else>{{ getCartonQuantity(item) }}</span>
                            </template>

                            <template v-slot:item.ml_info="{ item }">
                                <v-tooltip bottom v-if="item.ml_model_used">
                                    <template v-slot:activator="{ on }">
                                        <v-chip
                                            x-small
                                            :color="getMLConfidenceColor(item.ml_confidence_level)"
                                            dark
                                            v-on="on"
                                        >
                                            <v-icon x-small left>mdi-brain</v-icon>
                                            {{ item.ml_confidence_level }}
                                        </v-chip>
                                    </template>
                                    <div>
                                        <strong>Modello:</strong> {{ item.ml_model_used }}<br>
                                        <strong>Predizione:</strong> {{ item.ml_predicted_quantity?.toFixed(2) }}/gg<br>
                                        <strong>Media storica:</strong> {{ item.avg_daily_sales?.toFixed(2) }}/gg<br>
                                        <strong>Trend ML:</strong> {{ item.ml_trend_factor ? ((item.ml_trend_factor - 1) * 100).toFixed(0) + '%' : 'N/A' }}
                                    </div>
                                </v-tooltip>
                                <span v-else class="grey--text">-</span>
                            </template>

                            <template v-slot:item.estimated_cost="{ item }">
                                {{ formatEuro(item.estimated_cost) }}
                            </template>
                        </v-data-table>
                    </v-tab-item>

                    <!-- Tab: By Rating -->
                    <v-tab-item>
                        <v-alert type="info" dense outlined class="ma-4">
                            <div class="d-flex flex-wrap">
                                <div class="mr-6">
                                    <strong>ABC (Valore):</strong>
                                    <span class="ml-2">A = Alto (80%)</span>
                                    <span class="ml-2">B = Medio (15%)</span>
                                    <span class="ml-2">C = Basso (5%)</span>
                                </div>
                                <div>
                                    <strong>FSN (Rotazione):</strong>
                                    <span class="ml-2">F = Fast (80%)</span>
                                    <span class="ml-2">S = Slow (20%)</span>
                                    <span class="ml-2">N = Non-moving</span>
                                </div>
                            </div>
                        </v-alert>

                        <v-expansion-panels v-if="report.items" class="ma-4">
                            <v-expansion-panel v-for="ratingGroup in groupedByRating" :key="ratingGroup.rating">
                                <v-expansion-panel-header>
                                    <v-row align="center" no-gutters>
                                        <v-col cols="4">
                                            <v-chip
                                                small
                                                :color="getRatingColor(ratingGroup.rating)"
                                                dark
                                            >
                                                {{ ratingGroup.rating }}
                                            </v-chip>
                                            <span class="ml-2 text-caption grey--text">{{ getRatingDescription(ratingGroup.rating) }}</span>
                                        </v-col>
                                        <v-col cols="2">
                                            <v-chip small>{{ ratingGroup.count }} prodotti</v-chip>
                                        </v-col>
                                        <v-col cols="3">
                                            Qtà: {{ ratingGroup.total_qty }}
                                        </v-col>
                                        <v-col cols="3">
                                            <strong>{{ formatEuro(ratingGroup.total_cost) }}</strong>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-data-table
                                        v-model="selectedItems"
                                        :headers="itemHeadersRating"
                                        :items="ratingGroup.items"
                                        dense
                                        hide-default-footer
                                        :items-per-page="-1"
                                        show-select
                                        item-key="id"
                                    >
                                        <template v-slot:item.ar_codart="{ item }">
                                            <router-link
                                                :to="{ name: 'product', query: { id: item.ar_codart } }"
                                                class="text-decoration-none"
                                            >
                                                <strong class="primary--text">{{ item.ar_codart }}</strong>
                                            </router-link>
                                        </template>

                                        <template v-slot:item.is_critical="{ item }">
                                            <v-icon small color="error" v-if="item.is_critical">mdi-alert</v-icon>
                                        </template>

                                        <template v-slot:item.days_of_stock="{ item }">
                                            <v-chip small :color="getDaysStockColor(item.days_of_stock)">
                                                {{ Math.round(item.days_of_stock) }} gg
                                            </v-chip>
                                        </template>

                                        <template v-slot:item.final_order_qty="{ item }">
                                            <strong class="primary--text">{{ item.final_order_qty || item.adjusted_order_qty }}</strong>
                                        </template>

                                        <template v-slot:item.carton_order_qty="{ item }">
                                            <v-tooltip bottom v-if="item.carton_qty > 1">
                                                <template v-slot:activator="{ on }">
                                                    <strong class="success--text" v-on="on">
                                                        {{ getCartonQuantity(item) }}
                                                    </strong>
                                                </template>
                                                <span>Cartone da {{ item.carton_qty }} pz</span>
                                            </v-tooltip>
                                            <span v-else>{{ getCartonQuantity(item) }}</span>
                                        </template>

                                        <template v-slot:item.estimated_cost="{ item }">
                                            {{ formatEuro(item.estimated_cost) }}
                                        </template>
                                    </v-data-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-tab-item>

                    <!-- Tab: Critical Products -->
                    <v-tab-item>
                        <v-data-table
                            v-if="report.items"
                            v-model="selectedItems"
                            :headers="itemHeadersGlobal"
                            :items="criticalProducts"
                            class="ma-4"
                            show-select
                            item-key="id"
                        >
                            <template v-slot:item.ar_codart="{ item }">
                                <router-link
                                    :to="{ name: 'product', query: { id: item.ar_codart } }"
                                    class="text-decoration-none"
                                >
                                    <strong class="primary--text">{{ item.ar_codart }}</strong>
                                </router-link>
                            </template>

                            <template v-slot:item.is_critical="{ item }">
                                <v-icon small color="error">mdi-alert</v-icon>
                            </template>

                            <template v-slot:item.rating="{ item }">
                                <v-tooltip bottom v-if="item.rating">
                                    <template v-slot:activator="{ on }">
                                        <v-chip
                                            small
                                            :color="getRatingColor(item.rating)"
                                            dark
                                            v-on="on"
                                        >
                                            {{ item.rating }}
                                        </v-chip>
                                    </template>
                                    <div>
                                        <strong>{{ getRatingDescription(item.rating) }}</strong><br>
                                        <span v-if="item.abc_class">ABC: {{ item.abc_class }} | FSN: {{ item.fsn_class }}</span><br>
                                        <span>Moltiplicatore: {{ item.rating_multiplier }}x</span>
                                    </div>
                                </v-tooltip>
                            </template>

                            <template v-slot:item.days_of_stock="{ item }">
                                <v-chip small color="error">
                                    {{ Math.round(item.days_of_stock) }} gg
                                </v-chip>
                            </template>

                            <template v-slot:item.final_order_qty="{ item }">
                                <strong class="primary--text">{{ item.final_order_qty || item.adjusted_order_qty }}</strong>
                            </template>

                            <template v-slot:item.carton_order_qty="{ item }">
                                <v-tooltip bottom v-if="item.carton_qty > 1">
                                    <template v-slot:activator="{ on }">
                                        <strong class="success--text" v-on="on">
                                            {{ getCartonQuantity(item) }}
                                        </strong>
                                    </template>
                                    <span>Cartone da {{ item.carton_qty }} pz</span>
                                </v-tooltip>
                                <span v-else>{{ getCartonQuantity(item) }}</span>
                            </template>

                            <template v-slot:item.estimated_cost="{ item }">
                                {{ formatEuro(item.estimated_cost) }}
                            </template>
                        </v-data-table>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </template>

        <!-- Error state -->
        <v-alert v-else type="error" class="ma-4">
            Report non trovato o errore nel caricamento.
            <v-btn text @click="goBack" class="ml-4">Torna alla lista</v-btn>
        </v-alert>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">Chiudi</v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ReorderReportDetail',
    data() {
        return {
            loading: false,
            exporting: false,
            report: null,
            reportDetails: null,
            tab: 0,
            search: '',
            ratingFilter: [],
            showOnlyCritical: false,
            selectedItems: [],
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            ratingOptions: [
                { text: 'New - Prodotto Nuovo', value: 'New' },
                { text: 'In osservazione - In valutazione', value: 'In osservazione' },
                { text: 'AF - Alto Valore + Fast', value: 'AF' },
                { text: 'AS - Alto Valore + Slow', value: 'AS' },
                { text: 'AN - Alto Valore + Non-moving', value: 'AN' },
                { text: 'BF - Medio Valore + Fast', value: 'BF' },
                { text: 'BS - Medio Valore + Slow', value: 'BS' },
                { text: 'BN - Medio Valore + Non-moving', value: 'BN' },
                { text: 'CF - Basso Valore + Fast', value: 'CF' },
                { text: 'CS - Basso Valore + Slow', value: 'CS' },
                { text: 'CN - Basso Valore + Non-moving', value: 'CN' },
            ],
            ratingDescriptions: {
                'New': 'Prodotto Nuovo: Attivo da meno di 30 giorni',
                'In osservazione': 'In Osservazione: Attivo da 30-90 giorni',
                'AF': 'Prodotto Stella: Alto valore economico e alta rotazione',
                'AS': 'Prodotto Premium: Alto valore, rotazione lenta',
                'AN': 'Problema: Alto valore ma non si muove',
                'BF': 'Buon Performer: Valore medio, alta rotazione',
                'BS': 'Standard: Valore e rotazione nella media',
                'BN': 'Da Ridurre: Valore medio, non si muove',
                'CF': 'Volume Driver: Basso valore ma alta rotazione',
                'CS': 'Da Valutare: Basso valore e rotazione lenta',
                'CN': 'Candidato Eliminazione: Basso valore, non si muove',
            },
            itemHeadersSupplier: [
                { text: '', value: 'is_critical', sortable: true, width: 40 },
                { text: 'Codice', value: 'ar_codart', sortable: true },
                { text: 'Descrizione', value: 'product_description', sortable: true },
                { text: 'Rating', value: 'rating', sortable: true, align: 'center' },
                { text: 'Stock', value: 'current_stock', sortable: true },
                { text: 'Media/gg', value: 'avg_daily_sales', sortable: true },
                { text: 'Giorni Stock', value: 'days_of_stock', sortable: true },
                { text: 'Qtà Finale', value: 'final_order_qty', sortable: true },
                { text: 'Qtà Cartoni', value: 'carton_order_qty', sortable: true },
                { text: 'Costo', value: 'estimated_cost', sortable: true },
            ],
            itemHeadersGlobal: [
                { text: '', value: 'is_critical', sortable: true, width: 40 },
                { text: 'Codice', value: 'ar_codart', sortable: true },
                { text: 'Descrizione', value: 'product_description', sortable: true },
                { text: 'Fornitore', value: 'supplier_name', sortable: true },
                { text: 'Rating', value: 'rating', sortable: true, align: 'center' },
                { text: 'Stock', value: 'current_stock', sortable: true },
                { text: 'Media/gg', value: 'avg_daily_sales', sortable: true },
                { text: 'Giorni Stock', value: 'days_of_stock', sortable: true },
                { text: 'Qtà Trad.', value: 'adjusted_order_qty', sortable: true },
                { text: 'Qtà ML', value: 'ml_suggested_order_qty', sortable: true },
                { text: 'Qtà Finale', value: 'final_order_qty', sortable: true },
                { text: 'Qtà Cartoni', value: 'carton_order_qty', sortable: true },
                { text: 'ML', value: 'ml_info', sortable: false, align: 'center', width: 80 },
                { text: 'Costo', value: 'estimated_cost', sortable: true },
            ],
            itemHeadersRating: [
                { text: '', value: 'is_critical', sortable: true, width: 40 },
                { text: 'Codice', value: 'ar_codart', sortable: true },
                { text: 'Descrizione', value: 'product_description', sortable: true },
                { text: 'Fornitore', value: 'supplier_name', sortable: true },
                { text: 'Stock', value: 'current_stock', sortable: true },
                { text: 'Media/gg', value: 'avg_daily_sales', sortable: true },
                { text: 'Giorni Stock', value: 'days_of_stock', sortable: true },
                { text: 'Qtà Finale', value: 'final_order_qty', sortable: true },
                { text: 'Qtà Cartoni', value: 'carton_order_qty', sortable: true },
                { text: 'Costo', value: 'estimated_cost', sortable: true },
            ],
        };
    },
    computed: {
        reportId() {
            return this.$route.params.id;
        },
        criticalProducts() {
            if (!this.report || !this.report.items) return [];
            return this.report.items.filter(item => item.is_critical);
        },
        filteredProducts() {
            if (!this.report || !this.report.items) return [];

            let filtered = this.report.items;

            if (this.ratingFilter && this.ratingFilter.length > 0) {
                filtered = filtered.filter(item => this.ratingFilter.includes(item.rating));
            }

            if (this.showOnlyCritical) {
                filtered = filtered.filter(item => item.is_critical);
            }

            return filtered;
        },
        groupedByRating() {
            if (!this.report || !this.report.items) return [];

            const groups = {};
            this.report.items.forEach(item => {
                const rating = item.rating || 'N/A';
                if (!groups[rating]) {
                    groups[rating] = {
                        rating: rating,
                        items: [],
                        count: 0,
                        total_qty: 0,
                        total_cost: 0
                    };
                }
                groups[rating].items.push(item);
                groups[rating].count++;
                groups[rating].total_qty += item.adjusted_order_qty || 0;
                groups[rating].total_cost += item.estimated_cost || 0;
            });

            const ratingOrder = ['AF', 'BF', 'CF', 'AS', 'BS', 'CS', 'AN', 'BN', 'CN', 'New', 'In osservazione', 'N/A'];
            return Object.values(groups).sort((a, b) => {
                const orderA = ratingOrder.indexOf(a.rating);
                const orderB = ratingOrder.indexOf(b.rating);
                return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
            });
        },
        filteredSuppliers() {
            if (!this.reportDetails || !this.reportDetails.suppliers) return [];

            return this.reportDetails.suppliers.map(supplier => {
                let filteredItems = supplier.items;

                if (this.ratingFilter && this.ratingFilter.length > 0) {
                    filteredItems = filteredItems.filter(item => this.ratingFilter.includes(item.rating));
                }

                if (this.showOnlyCritical) {
                    filteredItems = filteredItems.filter(item => item.is_critical);
                }

                const total_qty = filteredItems.reduce((sum, item) => sum + (item.adjusted_order_qty || 0), 0);
                const total_cost = filteredItems.reduce((sum, item) => sum + (item.estimated_cost || 0), 0);
                const critical_count = filteredItems.filter(item => item.is_critical).length;

                return {
                    ...supplier,
                    items: filteredItems,
                    products_count: filteredItems.length,
                    total_qty: total_qty,
                    total_cost: total_cost,
                    critical_count: critical_count
                };
            }).filter(supplier => supplier.items.length > 0);
        },
        filteredTotals() {
            if (!this.filteredSuppliers) return { qty: 0, cost: 0, products: 0 };

            return this.filteredSuppliers.reduce((totals, supplier) => {
                return {
                    qty: totals.qty + supplier.total_qty,
                    cost: totals.cost + supplier.total_cost,
                    products: totals.products + supplier.products_count
                };
            }, { qty: 0, cost: 0, products: 0 });
        }
    },
    mounted() {
        this.loadReport();
    },
    methods: {
        async loadReport() {
            this.loading = true;
            try {
                const response = await axios.get(`/api/reorder/reports/${this.reportId}`);
                this.report = response.data;

                const detailsResponse = await axios.get(`/api/reorder-reports/${this.reportId}/suppliers`);
                this.reportDetails = detailsResponse.data;
            } catch (error) {
                console.error('Error loading report:', error);
                this.showSnackbar('Errore nel caricamento del report', 'error');
            } finally {
                this.loading = false;
            }
        },
        goBack() {
            this.$router.push({ name: 'reorder-reports' });
        },
        getCartonQuantity(item) {
            const cartonQty = item.carton_qty || 1;
            const orderQty = item.final_order_qty || item.adjusted_order_qty || 0;
            if (cartonQty <= 1) return orderQty;
            return Math.ceil(orderQty / cartonQty) * cartonQty;
        },
        async exportExcel() {
            this.exporting = true;
            try {
                const params = new URLSearchParams();

                // View type based on current tab
                const viewTypes = ['by_supplier', 'all', 'by_rating', 'critical'];
                params.append('view_type', viewTypes[this.tab] || 'all');

                // Add rating filters
                if (this.ratingFilter && this.ratingFilter.length > 0) {
                    this.ratingFilter.forEach(r => params.append('rating_filter[]', r));
                }

                // Add critical filter
                if (this.showOnlyCritical) {
                    params.append('only_critical', '1');
                }

                // Add selected items (if any)
                if (this.selectedItems.length > 0) {
                    this.selectedItems.forEach(item => params.append('selected_items[]', item.id));
                }

                // Trigger download
                window.location.href = `/api/reorder/reports/${this.reportId}/excel?${params.toString()}`;
                this.showSnackbar('Download Excel avviato', 'success');
            } catch (error) {
                console.error('Error exporting:', error);
                this.showSnackbar('Errore nell\'esportazione', 'error');
            } finally {
                this.exporting = false;
            }
        },
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('it-IT');
        },
        formatEuro(value) {
            if (!value) return '€0,00';
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value);
        },
        getStatusColor(status) {
            const colors = {
                'pending': 'orange',
                'generated': 'blue',
                'exported': 'green',
                'completed': 'green',
            };
            return colors[status] || 'grey';
        },
        getStatusLabel(status) {
            const labels = {
                'pending': 'In Attesa',
                'generated': 'Generato',
                'exported': 'Esportato',
                'completed': 'Completato',
            };
            return labels[status] || status;
        },
        getDaysStockColor(days) {
            if (days < 7) return 'error';
            if (days < 14) return 'warning';
            return 'success';
        },
        getRatingColor(rating) {
            const colors = {
                'AF': 'green darken-2',
                'AS': 'green lighten-1',
                'AN': 'green lighten-3',
                'BF': 'blue darken-1',
                'BS': 'blue lighten-1',
                'BN': 'blue lighten-3',
                'CF': 'orange darken-1',
                'CS': 'orange lighten-1',
                'CN': 'red darken-1',
                'A': 'green darken-1',
                'B': 'light-green darken-1',
                'C': 'orange darken-1',
                'D': 'red darken-1',
                'New': 'blue lighten-1',
                'In osservazione': 'purple lighten-1'
            };
            return colors[rating] || 'grey';
        },
        getRatingDescription(rating) {
            return this.ratingDescriptions[rating] || rating;
        },
        getMLConfidenceColor(confidence) {
            const colors = {
                'HIGH': 'green darken-1',
                'MEDIUM': 'orange darken-1',
                'LOW': 'red darken-1'
            };
            return colors[confidence] || 'grey';
        },
        showSnackbar(text, color = 'success') {
            this.snackbarText = text;
            this.snackbarColor = color;
            this.snackbar = true;
        },
        resetFilters() {
            this.ratingFilter = [];
            this.showOnlyCritical = false;
            this.search = '';
        }
    }
};
</script>

<style scoped>
.v-expansion-panel-header {
    padding: 12px 24px;
}
</style>
